import { push } from 'connected-react-router'
import { stringify } from 'query-string'
import { Action, Middleware } from 'redux'
import { isActionOf } from 'typesafe-actions'
import {
    getAddConfigurationUrl,
    getConfigurationByVehiclecodeApiUrl,
    getConfigurationUrl,
    getRemoveConfigurationUrl,
    getResetConfigurationApiUrl,
    getBuildabilityStatusUrl as getConfigurationBuildabilityStatusUrl,
} from '../../../constants/apiUrls'
import { ROUTE_OVERVIEW } from '../../../constants/routes'
import { resetAudiExclusiveDataState, updateAudiExclusiveSelection } from '../../actions/app/audiExclusiveApp.actions'

import { selectCarline } from '../../actions/app/carlineSelection.actions'
import { fetchCart } from '../../actions/app/cart.actions'
import {
    fetchConfiguration,
    fetchConfigurationAsync,
    getConfigurationByVehicleCode,
    getConfigurationByVehicleCodeAsync,
    putConfiguration,
    putConfigurationAsync,
    resetConfiguration,
    resetConfigurationAsync,
    setConfigurationDataState,
    checkConfigurationBuildabilityStatus,
    checkConfigurationBuildabilityStatusAsync,
    addConfigurationBuildabilityStatus,
    configurationIsResetOnInit,
} from '../../actions/app/configuration.actions'
import { acceptRescueConflictSolution } from '../../actions/app/conflictSolution.actions'
import { fetchEquipmentGroups } from '../../actions/app/equipmentGroups.actions'
import { resetVehicleCodeInState } from '../../actions/app/initParameters.actions'
import { resetConfigurationProcess } from '../../actions/app/reset.actions'
import { resetVehicleCodeStatus } from '../../actions/app/vehicleCode.actions'
import { fetchVisualization } from '../../actions/app/visualization.actions'
import { ApiRequest } from '../../apiRequest'
import selectedCarlineIdSelector from '../../selectors/carlines/selectedCarlineIdSelector'
import excludedPartsSelector from '../../selectors/excludedPartsSelector'
import isAudiExclusiveEnabledSelector from '../../selectors/feature-scope/isAudiExclusiveEnabledSelector'
import { resetVwnRecommendationsForModelData } from '../../actions/app/vwnRecommendation.actions'
import { resetAiRecommendationsForModelData } from '../../actions/app/aiRecommendation.actions'
import { fetchSummary } from '../../actions/app/summary.actions'
import currentPathnameSelector from '../../selectors/router/currentPathnameSelector'
import { createLatestRequestTracker } from './helpers/latestRequestTracker'

type LatestRequestTracker = ReturnType<typeof createLatestRequestTracker>

const configurationTrackers = new WeakMap<object, LatestRequestTracker>()
const getConfigurationTracker = (store: object): LatestRequestTracker => {
    let tracker = configurationTrackers.get(store)
    if (!tracker) {
        tracker = createLatestRequestTracker()
        configurationTrackers.set(store, tracker)
    }
    return tracker
}

// eslint-disable-next-line max-len
const configurationDataMiddleware =
    (apiRequest: ApiRequest): Middleware =>
    (store) =>
    (next) =>
    (action) => {
        next(action)
        const { dispatch, getState } = store
        const latestConfigurationRequest = getConfigurationTracker(store)

        if (isActionOf(fetchConfigurationAsync.request, action) || isActionOf(putConfigurationAsync.request, action)) {
            const causedBy = (action as { meta?: { causedBy?: Action } }).meta?.causedBy
            if (causedBy) {
                latestConfigurationRequest.markIssued(causedBy)
            }
        }

        if (isActionOf(fetchConfiguration, action)) {
            apiRequest(
                {
                    options: {
                        url: getConfigurationUrl(),
                        method: 'GET',
                    },
                    asyncActions: fetchConfigurationAsync,
                    causedBy: action,
                },
                dispatch,
            )
        }

        if (isActionOf(fetchConfigurationAsync.success, action)) {
            if (!latestConfigurationRequest.isLatest(action)) {
                return
            }
            const configurationData = action.payload

            dispatch(setConfigurationDataState(configurationData))

            const selectedCarlineId = selectedCarlineIdSelector(getState())

            if (configurationData.carlineId && selectedCarlineId !== configurationData.carlineId) {
                dispatch(
                    selectCarline(configurationData.carlineId, {
                        forwardToModelPage: false,
                    }),
                )
            }
        }

        if (isActionOf(putConfiguration, action)) {
            const { id, method } = action.payload
            const getUrl = method === 'add' ? getAddConfigurationUrl : getRemoveConfigurationUrl

            apiRequest(
                {
                    options: {
                        url: getUrl(id),
                        method: 'PATCH',
                    },
                    asyncActions: putConfigurationAsync,
                    causedBy: action,
                },
                dispatch,
            )
        }

        if (isActionOf(putConfigurationAsync.success, action)) {
            const configurationData = action.payload
            const isAudiExclusiveEnabled = isAudiExclusiveEnabledSelector(getState())

            dispatch(setConfigurationDataState(configurationData))

            if (!['CONFLICT', 'MODEL_CONFLICT'].includes(configurationData.configurationState)) {
                if (isAudiExclusiveEnabled) {
                    dispatch(updateAudiExclusiveSelection())
                } else {
                    dispatch(fetchEquipmentGroups())
                    dispatch(fetchCart())
                    dispatch(fetchVisualization())
                    if (currentPathnameSelector(getState()) === ROUTE_OVERVIEW) {
                        dispatch(fetchSummary())
                    }
                }
            }

            // Is Rescue Conflict?
            if (
                (action as any)?.meta?.causedBy &&
                isActionOf(acceptRescueConflictSolution, (action as any)?.meta?.causedBy)
            ) {
                dispatch(resetVehicleCodeInState())
                dispatch(resetVehicleCodeStatus())
                dispatch(fetchConfiguration())
                dispatch(push(ROUTE_OVERVIEW))
            }
        }

        if (isActionOf(resetConfiguration, action)) {
            apiRequest(
                {
                    options: {
                        url: getResetConfigurationApiUrl(),
                        method: 'POST',
                    },
                    asyncActions: {
                        request: resetConfigurationAsync.request,
                        success: action.meta?.onSuccess || resetConfigurationAsync.success,
                        failure: action.meta?.onFailure || resetConfigurationAsync.failure,
                    },
                    causedBy: action,
                },
                dispatch,
            )
        }

        const isResetOnInit = isActionOf(configurationIsResetOnInit, action)

        if (isResetOnInit || isActionOf(resetConfigurationAsync.success, action)) {
            dispatch(resetConfigurationProcess({ navigate: !isResetOnInit }))
            dispatch(resetAudiExclusiveDataState())
            dispatch(resetVwnRecommendationsForModelData())
            dispatch(resetAiRecommendationsForModelData())
        }

        if (isActionOf(getConfigurationByVehicleCode, action)) {
            const vehicleCode = action.payload

            const excludeParts = excludedPartsSelector(getState())
            const requestParams = stringify(
                { excludeParts },
                {
                    skipNull: true,
                },
            )

            apiRequest(
                {
                    options: {
                        url: getConfigurationByVehiclecodeApiUrl(vehicleCode, requestParams),
                        method: 'GET',
                    },
                    asyncActions: {
                        request: getConfigurationByVehicleCodeAsync.request,
                        success: action.meta?.onSuccess || getConfigurationByVehicleCodeAsync.success,
                        failure: action.meta?.onFailure || getConfigurationByVehicleCodeAsync.failure,
                    },
                    causedBy: action,
                },
                dispatch,
            )
        }

        if (isActionOf(checkConfigurationBuildabilityStatus, action)) {
            const configurationString = action.payload

            apiRequest(
                {
                    options: {
                        url: getConfigurationBuildabilityStatusUrl(configurationString),
                        method: 'POST',
                    },
                    asyncActions: checkConfigurationBuildabilityStatusAsync,
                    causedBy: action,
                },
                dispatch,
            )
        }

        if (isActionOf(checkConfigurationBuildabilityStatusAsync.success, action)) {
            dispatch(addConfigurationBuildabilityStatus(action.payload))
        }

        if (isActionOf(checkConfigurationBuildabilityStatusAsync.failure, action)) {
            const failedConfigurationString = (action as any).meta.causedBy.payload
            dispatch(addConfigurationBuildabilityStatus({
                configurationString: failedConfigurationString,
                status: 'UNKNOWN',
            }))
        }
    }

export default configurationDataMiddleware
